/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../utils/supabaseClient.js";
import { takeAuthRedirect } from "../utils/authRedirectHash.js";
import BrandLogo from "./BrandLogo.jsx";

export default function Login() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [magic, setMagic]       = useState(false);
  // A dead email link most often ends up HERE, not on /reset-password: when the
  // redirect falls back to the site root, RequireAuth bounces to /login and the
  // browser autofills a saved password — which is precisely why the failure read
  // as "nothing happened" (backlog item 63).
  const [status, setStatus]     = useState(() => {
    const msg = takeAuthRedirect()?.message;
    return msg ? { ok: false, msg } : null;
  });
  const [busy, setBusy]         = useState(false);
  const navigate = useNavigate();
  const loc      = useLocation();

  const after = loc.state?.from?.pathname || "/";

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      if (magic) {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
        });
        if (error) throw error;
        setStatus({ ok: true, msg: "Check your email for a magic link." });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate(after, { replace: true });
      }
    } catch (err) {
      setStatus({ ok: false, msg: err.message || "Sign in failed." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <section className="auth-card">
        <div className="auth-logo">
          <BrandLogo />
        </div>
        <h1>Welcome to Brand Command</h1>

        <form onSubmit={handleSubmit}>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          {!magic && (
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
          )}
          {!magic && (
            <div className="auth-row">
              <Link className="link" to="/forgot-password">Forgot password?</Link>
            </div>
          )}
          <label className="checkbox">
            <input type="checkbox" checked={magic} onChange={(e) => setMagic(e.target.checked)} />
            Email me a magic link instead
          </label>
          <button className="btn" type="submit" disabled={busy}>
            {busy ? "Working..." : magic ? "Send magic link" : "Log in"}
          </button>
        </form>

        {status && <p className={status.ok ? "success" : "error"}>{status.msg}</p>}

        <p className="auth-foot muted">
          No account? <Link to="/signup">Start your free trial</Link>
        </p>
      </section>
    </div>
  );
}
